import React, { useCallback, useState } from 'react'
import { ArrowBackIcon, WarningOutlineIcon } from '@toasttab/buffet-pui-icons'
import { gPlacesAPIKey } from 'config/index'
import {
  dummyAddressValue,
  normalizeGoogleAddress
} from 'src/public/components/online_ordering/addressUtils'
import { ModalCloseButton } from 'src/shared/components/common/modal/Modal'
import { t } from 'src/translations'
import Image from 'shared/components/common/Image'
import InlinePlacesAutocomplete from 'shared/components/common/location_search/InlinePlacesAutocomplete'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import LoadingSpinnerOverlay from 'shared/components/common/loading_spinner/LoadingSpinnerOverlay'
import { useFlags } from 'launchdarkly-react-client-sdk'
import {
  getCountryAddressConfig,
  getMissingRequiredFieldErrors,
  normalizeGoogleAddress as normalizeGoogleAddressForIntl,
  formatDeliveryAddress
} from 'src/public/components/online_ordering/address_configs'

/**
 * Props for AddressSearchScreen
 * Generic to work with any address type and flow
 */
export interface AddressSearchScreenProps<
  TScreen extends string,
  TAddress,
  TNormalizedAddress = any
> {
  goToScreen: (screen: TScreen, address?: TAddress | null) => void
  closeModal: () => void
  address: TAddress | null
  allowBackNav: boolean
  errorMsg?: string
  backNavScreen?: TScreen
  nextScreen: TScreen
  manualAddressScreen?: TScreen
  invalidAddressScreen?: TScreen
  formatAddress: (
    normalizedAddress: TNormalizedAddress,
    existingAddress?: TAddress | null
  ) => TAddress
  headerText: string
  helperText?: string
}

/**
 * AddressSearchScreen - Shared component for searching addresses via Google Places
 * This screen is generic and works with both fulfillment and account flows.
 */
const AddressSearchScreen = <
  TScreen extends string,
  TAddress,
  TNormalizedAddress = any
>({
  goToScreen,
  closeModal,
  address,
  allowBackNav,
  errorMsg = '',
  backNavScreen,
  nextScreen,
  manualAddressScreen,
  invalidAddressScreen,
  formatAddress,
  headerText,
  helperText = ''
}: AddressSearchScreenProps<TScreen, TAddress, TNormalizedAddress>) => {
  const { selectedLocation, i18n } = useRestaurant()
  const [loading, setLoading] = useState(false)
  const [showManualButton, setShowManualButton] = useState(false)
  const countryAddressConfig = getCountryAddressConfig(i18n?.country)
  const { intlAddressCountryLogicErrorHandling } = useFlags()

  /**
   * validation error state only pertinent to individual screen render lifecycles.
   * this validation error reflects issues upstream from delivery distance validation
   * of an address and restaurant couple (i.e. google auto complete failures)
   */
  const [validationError, setValidationError] = useState<string>(errorMsg || '')

  const handlePlaceSelected = useCallback(
    async (place: google.maps.places.PlaceResult) => {
      const addressHasStreetNumber = place.address_components?.some(
        (component) => component.types.includes('street_number')
      )

      if (
        (!intlAddressCountryLogicErrorHandling ||
          countryAddressConfig.requiresStreetNumber) &&
        !addressHasStreetNumber
      ) {
        if (intlAddressCountryLogicErrorHandling) setShowManualButton(true)
        return setValidationError(
          t('address-search-screen.enter-valid-address-with-street-number')
        )
      }

      const normalizedAddress = intlAddressCountryLogicErrorHandling
        ? normalizeGoogleAddressForIntl(place, i18n?.country)
        : normalizeGoogleAddress(place)

      if (intlAddressCountryLogicErrorHandling) {
        const requiredFieldErrors = getMissingRequiredFieldErrors(
          normalizedAddress,
          i18n?.country
        )

        if (requiredFieldErrors.length > 0) {
          setShowManualButton(true)
          return setValidationError(
            // if there are multiple errors, just show the first one
            t(requiredFieldErrors[0])
          )
        }
      } else if (
        normalizedAddress?.zipCode === dummyAddressValue &&
        (i18n?.country === 'IE' || i18n?.country === 'GB')
      ) {
        return setValidationError(
          t(
            `address-search-screen.enter-valid-address-with-${
              i18n.country === 'IE' ? 'eircode' : 'postcode'
            }`
          )
        )
      }

      if (selectedLocation?.externalId && normalizedAddress) {
        try {
          const formattedAddress = formatAddress(
            normalizedAddress as TNormalizedAddress,
            address
          )
          setLoading(true)
          goToScreen(nextScreen, formattedAddress)
          setLoading(false)
        } catch (_) {
          setLoading(false)
        }
      } else if (invalidAddressScreen) {
        goToScreen(invalidAddressScreen as TScreen, null)
      } else {
        setValidationError(t('address-search-screen.trouble-verifying-address'))
      }
    },
    [
      selectedLocation?.externalId,
      i18n?.country,
      goToScreen,
      address,
      nextScreen,
      invalidAddressScreen,
      formatAddress,
      intlAddressCountryLogicErrorHandling,
      countryAddressConfig
    ]
  )

  const defaultInput = address
    ? formatDeliveryAddress(
        (address as any).deliveryInfo,
        {
          full: true
        },
        intlAddressCountryLogicErrorHandling
      )
    : ''

  // if we have a tentative delivery address, keep in scope during transition back
  const backNavHandler = useCallback(() => {
    if (backNavScreen) {
      if (address) {
        goToScreen(backNavScreen)
      } else {
        goToScreen(backNavScreen, null)
      }
    }
  }, [address, backNavScreen, goToScreen])

  return (
    <div data-testid='address-search-screen' className='addressSearchScreen'>
      <div className='modalHeader'>
        {allowBackNav && backNavScreen && (
          <button
            data-testid='address-search-back-btn'
            aria-label={t('fulfillment-general.back')}
            type='button'
            className='backArrow'
            onClick={backNavHandler}
          >
            <ArrowBackIcon />
          </button>
        )}
        <h2>{headerText}</h2>
        <ModalCloseButton onClose={closeModal} />
      </div>
      {validationError && (
        <div className='warningBanner' data-testid='address-search-error'>
          <WarningOutlineIcon size='xs' accessibility='decorative' />
          <span>{validationError}</span>
        </div>
      )}
      <div className='modalContent'>
        <div className='addressInputContainer'>
          <InlinePlacesAutocomplete
            id='delivery-address-input-autocomplete'
            defaultValue={defaultInput}
            placeholder={t('fulfillment-general.enter-delivery-address')}
            helperText={helperText}
            apiKey={gPlacesAPIKey}
            requireZip={
              intlAddressCountryLogicErrorHandling
                ? countryAddressConfig.fields.postalCode.required
                : true
            }
            autoFocus
            onValueChange={() => {
              if (validationError) setValidationError('')
            }}
            onError={() => {
              if (intlAddressCountryLogicErrorHandling)
                setShowManualButton(true)
              return setValidationError(
                t('address-search-screen.trouble-verifying-address')
              )
            }}
            onPlaceSelected={handlePlaceSelected}
            triggerManualAddress={
              manualAddressScreen
                ? () => goToScreen(manualAddressScreen as TScreen)
                : undefined
            }
            showManualButton={showManualButton}
            locationBias={
              selectedLocation?.lat && selectedLocation?.long
                ? { lat: selectedLocation.lat, long: selectedLocation.long }
                : undefined
            }
            normalized
          />
        </div>
        <div className='attribution'>
          <span>{t('address-search-screen.powered-by')}</span>
          <Image
            src='icons/google_on_white_hdpi.png'
            alt={t('address-search-screen.google')}
          />
        </div>
      </div>
      {loading && <LoadingSpinnerOverlay withSubtleBorderRadius />}
    </div>
  )
}

export default AddressSearchScreen
